import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Check, Ellipsis } from 'lucide-react';

import { Images } from '@codes/assets';
import { useTheme } from '@codes/theme';
import { Button } from '@codes/ui-kit';

import type { ChatView } from '@lemoncloud/ssocio-chatbots-api';

interface ConversationItemProps {
    conversation: ChatView;
    onDelete: () => void;
    onTogglePin: () => void;
    onConversationClick: () => void;
    isCurrentChat: boolean;
    isPinned: boolean;
    isDisabled?: boolean;
}

export const ConversationItem = ({
    conversation,
    onDelete,
    onTogglePin,
    onConversationClick,
    isCurrentChat,
    isDisabled = false,
    isPinned,
}: ConversationItemProps) => {
    const { t } = useTranslation(['translation', 'box']);
    const { isDarkTheme } = useTheme();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const handleDeleteClick = () => {
        setDropdownOpen(false);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        onDelete();
        setDeleteDialogOpen(false);
    };

    const handleConversationClick = (e: React.MouseEvent) => {
        if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        onConversationClick();
    };

    return (
        <>
            <li className="relative p-[6px] text-xs rounded-[5px] max-h-10 flex items-center justify-between gap-2 transition-all duration-200 text-text-800 bg-white dark:bg-[#02060E] hover:bg-gray-50 dark:hover:bg-[#1a1a1a]">
                <div
                    className="flex items-center gap-[9px] flex-1 min-w-0 cursor-pointer"
                    onClick={handleConversationClick}
                    aria-disabled={isDisabled}
                >
                    {isPinned && (
                        <img
                            src={isDarkTheme ? Images.notice : Images.notice}
                            alt="pinned icon"
                            className="w-[17px] h-[17px] shrink-0"
                        />
                    )}
                    <div
                        className="line-clamp-2 text-xs flex-1"
                        style={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {conversation.name || t('box:conversation.new_chat')}
                    </div>

                    {isCurrentChat && <Check className="w-4 h-4 text-[#7932FF] dark:text-[#8F19F6] shrink-0" />}
                </div>

                <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="p-[2px] h-auto group shrink-0">
                            <Ellipsis className="text-[#9FA2A7] group-hover:text-text transition-colors duration-200" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="px-3 bg-chatbot-card">
                        <DropdownMenuItem className="text-xs" onClick={onTogglePin}>
                            {isPinned ? t('box:conversation.unpin') : t('box:conversation.pin')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-[#F4F5F5] dark:bg-[#53555B] h-px mx-0 my-[2px]" />
                        <DropdownMenuItem className="text-xs text-destructive" onClick={handleDeleteClick}>
                            {t('box:common.delete')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </li>

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('box:conversation.delete_confirmation_title')}</AlertDialogTitle>
                    </AlertDialogHeader>
                    <AlertDialogDescription className="text-center">
                        {t('box:conversation.delete_confirmation_description')}
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('box:common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmDelete}>{t('box:common.delete')}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
